import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import MuiLink from '@material-ui/core/Link';
import Skeleton from '@material-ui/lab/Skeleton';
import CredentialIntegrationForm from '../CredentialIntegrationForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import { integrations as integrationsApi } from '@afosto/apps/api/Integrations';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import translations from './translations';

const Credentialitem = ({ appCode, divider, image, name, path, showAction }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [credentials, setCredentials] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [isFetchingCredentials, setIsFetchingCredentials] = useState(true);
  const [showCredentialsDrawer, setShowCredentialsDrawer] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchCredentials = async () => {
      try {
        setIsFetchingCredentials(true);

        const response = await profileApi.getProfile(path);
        const credentialsData = response?.data ?? null;

        if (credentialsData?.integration_id) {
          const integrationsResponse = await integrationsApi.getIntegration(
            credentialsData.integration_id,
          );
          const integrationData = integrationsResponse?.data;

          if (isMounted) {
            setIntegration(integrationData);
          }
        }

        if (isMounted) {
          setCredentials(credentialsData);
        }
      } catch (err) {
        if (isMounted) {
          setCredentials(null);
        }
      } finally {
        setIsFetchingCredentials(false);
      }
    };

    if (path) {
      fetchCredentials();
    }

    return () => {
      isMounted = false;
    };
  }, [path]);

  const handleSaveCredentials = async ({ integrationId }) => {
    try {
      const newCredentials = integrationId ? { integration_id: integrationId } : {};
      await profileApi.updateProfile(path, newCredentials);

      if (integrationId && integrationId !== integration?.id) {
        const integrationsResponse = await integrationsApi.getIntegration(integrationId);
        const integrationData = integrationsResponse?.data ?? null;
        setIntegration(integrationData);
      } else if (!integrationId) {
        setIntegration(null);
      }

      setCredentials(newCredentials);
      setShowCredentialsDrawer(false);
      enqueueSnackbar(intl.formatMessage(translations.feedback.credentialsUpdated), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.credentialsNotUpdated), {
        variant: 'error',
      });
    }
  };

  if (isFetchingCredentials) {
    return (
      <ListItem divider={divider}>
        <ListItemAvatar>
          <Skeleton variant="circle" width={40} height={40} />
        </ListItemAvatar>
        <ListItemText
          primary={<Skeleton variant="text" />}
          secondary={<Skeleton variant="text" />}
        />
      </ListItem>
    );
  }

  return (
    <>
      <ListItem divider={divider}>
        <ListItemAvatar>
          <Avatar variant="rounded" src={image} />
        </ListItemAvatar>
        <ListItemText
          primary={name}
          secondary={
            integration ? (
              <MuiLink component={Link} to={`/integrations/${integration.id}`}>
                {integration.name}
              </MuiLink>
            ) : (
              intl.formatMessage(translations.notConfigured)
            )
          }
          secondaryTypographyProps={{ noWrap: true }}
        />
        {showAction && (
          <Box pl={1}>
            <Button variant="outlined" onClick={() => setShowCredentialsDrawer(true)} size="small">
              {intl.formatMessage(translations.edit)}
            </Button>
          </Box>
        )}
      </ListItem>
      <FormDrawer
        title={name}
        isOpen={showCredentialsDrawer}
        onClose={() => setShowCredentialsDrawer(false)}
        drawerWidth="400px"
      >
        <CredentialIntegrationForm
          appCode={appCode}
          credentials={credentials}
          onCancel={() => setShowCredentialsDrawer(false)}
          onSubmit={handleSaveCredentials}
        />
      </FormDrawer>
    </>
  );
};

Credentialitem.propTypes = {
  appCode: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  divider: PropTypes.bool,
  image: PropTypes.string,
  showAction: PropTypes.bool,
};

Credentialitem.defaultProps = {
  divider: true,
  image: undefined,
  showAction: false,
};

export default Credentialitem;
